import { Pencil, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InventoryPhoto } from "@/components/InventoryPhoto";
import { PrintButton } from "@/components/PrintButton";
import { printDetailSheet } from "@/lib/print";

const inventoryPrintFields = (item) => [
  { label: "Category", value: item.category },
  { label: "Type", value: item.item_type },
  {
    label: "Recorded quantity",
    value: [item.quantity, item.units].filter(Boolean).join(" "),
  },
  { label: "Last verified", value: item.verified_quantity ?? "Not verified" },
  { label: "Location", value: item.location },
  { label: "Condition", value: item.condition },
  { label: "Supplier", value: item.supplier },
  { label: "Hazard information", value: item.hazard_info },
  { label: "CLEAPSS Hazcard Number", value: item.hazcard_number },
  { label: "Storage", value: item.storage_info },
  { label: "Safety information", value: item.safety_info },
  { label: "YouTube link", value: item.youtube_url },
  {
    label: "Double-booking alert",
    value: item.conflict_alert ? "Enabled" : "Not enabled",
  },
];

const Detail = ({ label, value, testId }) => (
  <div data-testid={testId}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="mt-0.5 text-sm font-medium">{value || "—"}</p>
  </div>
);

export const InventoryDetailsDialog = ({ item, canEdit, documents, onClose, onEdit }) => (
  <Dialog open={Boolean(item)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        data-testid="inventory-details-dialog"
      >
      <DialogHeader>
        <div className="flex items-center justify-between gap-3 pr-7">
          <DialogTitle>{item.name}</DialogTitle>
          <PrintButton
            label="Print item"
            size="sm"
            testId="inventory-details-print-button"
            onClick={() => printDetailSheet(item.name, inventoryPrintFields(item))}
          />
          {canEdit && (
            <Button
              onClick={onEdit}
              size="sm"
              variant="outline"
              data-testid="inventory-details-edit-button"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Edit item
            </Button>
          )}
          {item.youtube_url && (
            <Button asChild size="sm" variant="outline" data-testid="inventory-youtube-link">
              <a href={item.youtube_url} target="_blank" rel="noreferrer">
                <Youtube className="mr-2 h-4 w-4" />
                Open on YouTube
              </a>
            </Button>
          )}
        </div>
      </DialogHeader>
      <div className="grid gap-6 sm:grid-cols-[minmax(12rem,0.8fr)_1.2fr]">
        <InventoryPhoto item={item} />
        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <Detail label="Category" value={item.category} testId="inventory-detail-category" />
          <Detail label="Type" value={item.item_type} testId="inventory-detail-type" />
          <Detail
            label="Recorded quantity"
            value={[item.quantity, item.units].filter(Boolean).join(" ")}
            testId="inventory-detail-quantity"
          />
          <Detail label="Location" value={item.location} testId="inventory-detail-location" />
          <Detail label="Condition" value={item.condition} testId="inventory-detail-condition" />
          <Detail label="Supplier" value={item.supplier} testId="inventory-detail-supplier" />
          <Detail
            label="Hazard information"
            value={item.hazard_info}
            testId="inventory-detail-hazard"
          />
          {item.category?.trim().toLowerCase() === "chemical" && (
            <Detail
              label="CLEAPSS Hazcard Number"
              value={item.hazcard_number}
              testId="inventory-detail-hazcard-number"
            />
          )}
          <Detail label="Storage" value={item.storage_info} testId="inventory-detail-storage" />
          <Detail
            label="Double-booking alert"
            value={item.conflict_alert ? "Enabled" : "Not enabled"}
            testId="inventory-detail-conflict-alert"
          />
        </div>
      </div>
      {documents}
    </DialogContent>
  </Dialog>
);